import { create } from 'zustand';
import { safeGetItem, safeSetItem } from '../lib/safeStorage';
import { isIOSSafari, isInstalled } from './installPrompt';
import { getVapidKey, postSubscription } from '../api/push';
import { putNotificationPref, useNotificationPrefs, type CategoryPrefDto } from '../api/notifications';

const ASKS_KEY = 'pp.push.asks';

// §11.11: "ask at most twice, ever" — same lifetime-cap shape installPrompt.ts's own MAX_ASKS
// uses for the install prompt.
export const MAX_PUSH_ASKS = 2;

// SCREENS.md "/me/notifications" push-on state's own copy, verbatim — shared here so
// NotificationSettingsScreen and PushPrePromptSheet never drift out of sync with each other (both
// list "the same 5 categories", per the Push pre-prompt sheet's own SCREENS.md row).
export const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  transactional: 'Trade offer received, auction outbid, auction won, guild invite',
  timers: "Training complete, forage ready — only for timers you explicitly asked to be told about",
  social: 'Someone favourited your Look, guild goal completed, Look of the Week',
  liveops: 'Event started, cosmetic rotation refreshed',
  arena: 'Someone challenged your Arena defence — whether your team held',
};

function readAsks(): number {
  const n = Number(safeGetItem(ASKS_KEY));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export type PushStatus = 'idle' | 'unsupported' | 'denied' | 'subscribed';

type PushState = {
  asks: number;
  status: PushStatus;
  recordAsk: () => void;
  setStatus: (status: PushStatus) => void;
  /** Test-only: re-reads localStorage after a test clears it — mirrors `useInstallStore.reset`'s
   *  own convention for module-level stores that need a clean slate between tests. Real app code
   *  never calls this. */
  reset: () => void;
};

/** Module-level store — same "survives independent of any one component's lifecycle" shape
 *  `installPrompt.ts`'s `useInstallStore` uses, needed here for the same reason: `subscribeFlow`
 *  can be called from either `PushPrePromptSheet` or (in principle) any future touch point, and
 *  `resubscribeOnBoot` runs once at app boot with no sheet mounted at all. */
export const usePushStore = create<PushState>((set, get) => ({
  asks: readAsks(),
  status: 'idle',
  recordAsk: () => {
    const next = get().asks + 1;
    safeSetItem(ASKS_KEY, String(next));
    set({ asks: next });
  },
  setStatus: (status) => set({ status }),
  reset: () => set({ asks: readAsks(), status: 'idle' }),
}));

/** "Not now" (either variant of the sheet) — always records an ask, same as `installPrompt.ts`'s
 *  own `dismiss()`. Exported standalone so `PushPrePromptSheet` doesn't need to reach into the
 *  store directly. */
export function recordAsk(): void {
  usePushStore.getState().recordAsk();
}

/**
 * Whether the in-app pre-prompt sheet may still be opened AMBIENTLY (a touch point offering it
 * unprompted, mid-flow) — the caller decides WHEN it's contextually relevant; this only decides
 * whether it's still allowed to at all. False once either:
 *  - the lifetime ask cap is spent (`asks >= MAX_PUSH_ASKS`), or
 *  - the OS permission has ever been denied — §11.11 "a denial is an answer": once denied, ambient
 *    offers stop permanently, regardless of the ask count. Checked two ways: this session's own
 *    `status` (set by `subscribeFlow` the instant `Notification.requestPermission()` resolves
 *    anything but `'granted'`) AND the live, persistent `Notification.permission` — the store's
 *    `status` only knows about denials that happened during THIS session, whereas a player who
 *    denied last session, closed the tab, and came back still has a real, live 'denied' permission
 *    the browser remembers on its own.
 *
 * Deliberately NOT consulted by the `/me/notifications` settings screen's own "Turn on
 * notifications" CTA — that's a deliberate visit, not an ambient nudge, and isn't gated by this
 * cap at all (see that screen's own comment). It DOES still honour a live denial, but via its own
 * `denied` state/copy, not this hook.
 */
export function usePushEligible(): boolean {
  const asks = usePushStore((s) => s.asks);
  const status = usePushStore((s) => s.status);
  const liveDenied = typeof Notification !== 'undefined' && Notification.permission === 'denied';
  return asks < MAX_PUSH_ASKS && status !== 'denied' && !liveDenied;
}

/**
 * §11.11 / Decision #4: "push disabled entirely for minors." The server's own `available` flag on
 * every category is a single account-wide bit (`NotificationService.prefs` — `!isMinorBand(...)`
 * applied uniformly, never per-category), so any one category's `available` reflects the whole
 * account; checking all of them is just defence against a partial/malformed response rather than
 * a real per-category question. Conservative while unknown (`prefs.data` not loaded yet, or the
 * fetch failed) — treated as "hide", the same C3 "assume the more restrictive case where unclear"
 * reasoning `NotificationService`'s own javadoc documents for UNKNOWN age bands: a touch point
 * flashing a push affordance for a heartbeat before correcting itself is worse than never
 * offering it to an adult a beat later, once prefs actually load.
 */
export function useIsPushBlockedForMinor(): boolean {
  const prefs = useNotificationPrefs();
  if (!prefs.data) return true;
  return prefs.data.categories.every((c) => !c.available);
}

/** Timers is the only category with a live sender in Phase 3 (SCREENS.md "/me/notifications"
 *  push-on row) — a companion is "push ready" only once actually subscribed AND that specific
 *  category is on, not merely once the OS permission was granted. */
export function usePushReady(): boolean {
  const status = usePushStore((s) => s.status);
  const prefs = useNotificationPrefs();
  const timersEnabled = prefs.data?.categories.find((c) => c.code === 'timers')?.enabled ?? false;
  return status === 'subscribed' && timersEnabled;
}

/** Only the 5 categories the account can actually see (§11.11/Decision #4 — empty for a minor
 *  account, whose every category comes back `available: false`). Shared by
 *  `PushPrePromptSheet`/`NotificationSettingsScreen` so both list exactly the same set, in the
 *  same server-supplied order (`sort`, already applied server-side). */
export function availableCategories(categories: CategoryPrefDto[] | undefined): CategoryPrefDto[] {
  return (categories ?? []).filter((c) => c.available);
}

/** SCREENS.md "/me/notifications" push-on defaults / Push pre-prompt sheet's own category-picker
 *  defaults: Transactional and Arena start checked (the two categories whose content pack sets
 *  `defaultEnabled: true` — a category left unchecked is never PUT by `subscribeFlow`, so it KEEPS
 *  the server default; showing either unchecked while the server default leaves it on would make
 *  the picker lie), Timers only when opened from a timer touch point (`timersPreChecked`),
 *  everything else starts unchecked. */
export function defaultCategoryChecked(code: string, timersPreChecked: boolean): boolean {
  if (code === 'transactional' || code === 'arena') return true;
  if (code === 'timers') return timersPreChecked;
  return false;
}

// Standard VAPID-key base64url → Uint8Array conversion for `PushManager.subscribe`'s
// `applicationServerKey` — no library for this one small piece of the Web Push spec.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function subscriptionKeys(sub: PushSubscription): { p256dh: string; auth: string } {
  const json = sub.toJSON();
  return { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' };
}

// QA 2026-09-23 (social F12): "Turn on" hung at "Turning on…" forever. On a build with no service
// worker (the dev server, any browser where registration failed) `navigator.serviceWorker.ready`
// never resolves, and nothing bounded it — nor the subscribe, nor the two posts. Every step after
// the OS prompt now races this; the prompt itself is deliberately unbounded (a person is reading it).
export const STEP_TIMEOUT_MS = 10_000;
export function withTimeout<T>(promise: Promise<T>, ms = STEP_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error('push step timed out')), ms);
    promise.then(
      (value) => { window.clearTimeout(id); resolve(value); },
      (err) => { window.clearTimeout(id); reject(err); },
    );
  });
}

export type SubscribeOutcome = 'subscribed' | 'denied' | 'unsupported' | 'unavailable' | 'failed';

/**
 * §11.11 "ONE OS permission prompt ever, always preceded by an in-app pre-prompt." This is the
 * ONLY call site in the app that may invoke `Notification.requestPermission()` — it exists
 * exclusively behind `PushPrePromptSheet`'s own "Turn on" button, never automatically.
 *
 * `categories` are the category codes the player left checked in the sheet — each gets an
 * explicit `PUT .../prefs` enabling it (categories left unchecked are simply never PUT, so they
 * keep whichever default/prior value the server already has for them).
 */
export async function subscribeFlow(categories: string[]): Promise<SubscribeOutcome> {
  if (!('PushManager' in window) || !('Notification' in window) || !navigator.serviceWorker) {
    usePushStore.getState().setStatus('unsupported');
    return 'unsupported';
  }

  // The key comes FIRST: a server with no VAPID pair (`{publicKey: ""}` — production today, and
  // every dev stack) has nothing to subscribe to, so the one-ever OS prompt must not be spent on
  // it. `unavailable`/`failed` leave the store's status alone — neither is an answer from the
  // person, so neither counts as an ask or a denial.
  let vapidKey: string;
  try {
    vapidKey = await withTimeout(getVapidKey());
  } catch {
    return 'failed';
  }
  if (!vapidKey) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    recordAsk();
    usePushStore.getState().setStatus('denied');
    return 'denied';
  }

  try {
    const registration = await withTimeout(navigator.serviceWorker.ready);
    const applicationServerKey = urlBase64ToUint8Array(vapidKey);
    const sub = await withTimeout(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey }));
    const { p256dh, auth } = subscriptionKeys(sub);
    await withTimeout(postSubscription({
      endpoint: sub.endpoint, p256dh, auth, tzOffsetMin: -new Date().getTimezoneOffset(),
    }));
    await withTimeout(Promise.all(categories.map((category) => putNotificationPref({ category, enabled: true }))));
  } catch {
    return 'failed';
  }

  usePushStore.getState().setStatus('subscribed');
  return 'subscribed';
}

/**
 * Called once from `App.tsx`'s boot effect. Ruling 7 (task-23-brief.md): "the client re-posts on
 * every app start so tz stays current" — a player's device timezone can change between sessions
 * (travel, DST) with no other signal that would tell the server. A no-op (never touches the
 * network) unless the OS permission is already `'granted'` — i.e. this never itself triggers the
 * permission prompt (that's `subscribeFlow`'s job alone) and never resurrects a subscription the
 * player has actively denied/revoked.
 */
export async function resubscribeOnBoot(): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (!navigator.serviceWorker) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return;
    const { p256dh, auth } = subscriptionKeys(sub);
    await postSubscription({
      endpoint: sub.endpoint, p256dh, auth, tzOffsetMin: -new Date().getTimezoneOffset(),
    });
    usePushStore.getState().setStatus('subscribed');
  } catch {
    // Best-effort, same as sw.ts's own `pushsubscriptionchange` handler — a boot-time hiccup
    // (offline, a transient 5xx) must never block the rest of app startup.
  }
}

// Re-exported so PushPrePromptSheet.tsx's iOS variant doesn't need a second import of
// installPrompt.ts's own detection helpers.
export { isIOSSafari, isInstalled };
